"use client";

import { useCallback } from "react";

import { ASSESSMENT_WORKFLOW_OPTIONS } from "@/constants";
import { useAviFilterStore } from "@/store/useAviFilterStore";
import type { AviPillar, DeadlineFilterStatus, WorkflowStatus } from "@/types";

import { useFilterScopeCascade } from "./useFilterScopeCascade";

const HOME_WORKFLOW_OPTIONS: WorkflowStatus[] = [...ASSESSMENT_WORKFLOW_OPTIONS];

interface UseAviFiltersOptions {
  role: string;
}

export function useAviFilters({ role }: UseAviFiltersOptions) {
  const {
    draft,
    setDraft,
    applyFilters: applyStoreFilters,
    reset,
    applied,
    syncDraftFromApplied: syncStoreDraftFromApplied,
    removeAppliedFilter,
  } = useAviFilterStore();

  const sanitizeWorkflowFilters = useCallback(
    (workflowFilters: WorkflowStatus[] | undefined) =>
      workflowFilters?.filter((status) => HOME_WORKFLOW_OPTIONS.includes(status)) ?? [],
    []
  );

  const hasAppliedFilters = Object.values(applied).some((value) =>
    Array.isArray(value) ? value.length > 0 : !!value
  );

  const hasDraftFilters =
    !!draft.selectedProject ||
    !!draft.selectedSite ||
    draft.selectedAssets.length > 0 ||
    !!draft.selectedStandard ||
    draft.deadlineFilters.length > 0 ||
    draft.workflowFilters.length > 0 ||
    draft.pillarFilters.length > 0;

  const cascade = useFilterScopeCascade({
    role,
    draft: {
      selectedProject: draft.selectedProject,
      selectedProjectLabel: draft.selectedProjectLabel,
      selectedSite: draft.selectedSite,
      selectedSiteLabel: draft.selectedSiteLabel,
      selectedAssets: draft.selectedAssets,
      selectedAssetLabels: draft.selectedAssetLabels,
      selectedStandard: draft.selectedStandard,
    },
    setDraft,
  });

  const handleStandardChange = useCallback(
    (value: string) => setDraft({ selectedStandard: value }),
    [setDraft]
  );

  const handleToggleDeadline = useCallback(
    (option: DeadlineFilterStatus) => {
      const next = draft.deadlineFilters.includes(option)
        ? draft.deadlineFilters.filter((current) => current !== option)
        : [...draft.deadlineFilters, option];
      setDraft({ deadlineFilters: next });
    },
    [draft.deadlineFilters, setDraft]
  );

  const handleToggleWorkflow = useCallback(
    (option: WorkflowStatus) => {
      const next = draft.workflowFilters.includes(option)
        ? draft.workflowFilters.filter((current) => current !== option)
        : [...draft.workflowFilters, option];
      setDraft({ workflowFilters: next });
    },
    [draft.workflowFilters, setDraft]
  );

  const applyFilters = useCallback(() => {
    const nextWorkflowFilters = sanitizeWorkflowFilters(draft.workflowFilters);

    if (nextWorkflowFilters.length !== draft.workflowFilters.length) {
      setDraft({ workflowFilters: nextWorkflowFilters });
    }

    applyStoreFilters();
  }, [applyStoreFilters, draft.workflowFilters, sanitizeWorkflowFilters, setDraft]);

  const syncDraftFromApplied = useCallback(() => {
    const nextWorkflowFilters = sanitizeWorkflowFilters(applied.workflow_status);

    if (nextWorkflowFilters.length !== (applied.workflow_status?.length ?? 0)) {
      removeAppliedFilter({
        workflow_status: nextWorkflowFilters.length ? nextWorkflowFilters : undefined,
      });
    }

    syncStoreDraftFromApplied();
    setDraft({ workflowFilters: nextWorkflowFilters });
  }, [
    applied.workflow_status,
    removeAppliedFilter,
    sanitizeWorkflowFilters,
    setDraft,
    syncStoreDraftFromApplied,
  ]);

  const handleTogglePillar = useCallback(
    (option: AviPillar) => {
      const next = draft.pillarFilters.includes(option)
        ? draft.pillarFilters.filter((current) => current !== option)
        : [...draft.pillarFilters, option];
      setDraft({ pillarFilters: next });
    },
    [draft.pillarFilters, setDraft]
  );

  return {
    draft,
    hasAppliedFilters,
    hasDraftFilters,
    reset,
    applyFilters,
    syncDraftFromApplied,
    handleStandardChange,
    handleToggleDeadline,
    handleToggleWorkflow,
    handleTogglePillar,
    ...cascade,
  };
}
